import { useCallback, useEffect, useRef } from 'react';

import { Box } from '@bonusly/design-system';

import { BIZY_PANEL_MAX_WIDTH, BIZY_PANEL_MIN_WIDTH, useBizySidePanelStore } from './store/use-bizy-side-panel-store';

interface BizySidePanelResizeHandleProps {
	panelLeftEdge: number;
}

export function BizySidePanelResizeHandle({ panelLeftEdge }: BizySidePanelResizeHandleProps) {
	const setWidth = useBizySidePanelStore((state) => state.setWidth);
	const setIsResizing = useBizySidePanelStore((state) => state.setIsResizing);
	const isDragging = useRef(false);

	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			isDragging.current = true;
			setIsResizing(true);
			document.body.style.cursor = 'col-resize';
			document.body.style.userSelect = 'none';

			const handleMouseMove = (moveEvent: MouseEvent) => {
				if (!isDragging.current) return;
				const newWidth = moveEvent.clientX - panelLeftEdge;
				setWidth(Math.max(BIZY_PANEL_MIN_WIDTH, Math.min(BIZY_PANEL_MAX_WIDTH, newWidth)));
			};

			const handleMouseUp = () => {
				isDragging.current = false;
				setIsResizing(false);
				document.body.style.cursor = '';
				document.body.style.userSelect = '';
				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleMouseUp);
			};

			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
		},
		[panelLeftEdge, setWidth, setIsResizing]
	);

	useEffect(() => {
		return () => {
			isDragging.current = false;
			setIsResizing(false);
			document.body.style.cursor = '';
			document.body.style.userSelect = '';
		};
	}, [setIsResizing]);

	return (
		<Box
			position="absolute"
			right={0}
			top={0}
			bottom={0}
			w="6px"
			cursor="col-resize"
			zIndex={1}
			onMouseDown={handleMouseDown}
			_hover={{ '& > div': { opacity: 1 } }}
			role="separator"
			aria-orientation="vertical"
			aria-label="Resize Bizy panel"
		>
			<Box
				position="absolute"
				right="2px"
				top="50%"
				transform="translateY(-50%)"
				w="3px"
				h="32px"
				borderRadius="full"
				bg="text.softest"
				opacity={0}
				transition="opacity 0.15s"
			/>
		</Box>
	);
}
